import type { Stop } from "../types";
import { findStops, type StopFindResult } from "../api/stopFind";

function stripStopNameForCity(name: string) {
  return String(name)
    .replace(/\s*[\(\[]?[A-ZÅÄÖ][\)\]]?$/, "")
    .trim();
}

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

function distanceMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
) {
  const R = 6_371_000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const la1 = toRad(aLat);
  const la2 = toRad(bLat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function findBestStopFindHit(stop: Stop, hits: StopFindResult[]) {
  if (!hits.length) return null;

  const baseName = stripStopNameForCity(stop.name).toLowerCase();

  const nameCandidates = hits.filter((hit) => {
    const place = String(hit.PlaceName ?? "").toLowerCase();

    return place === baseName || place.startsWith(`${baseName},`);
  });

  const candidates = nameCandidates.length ? nameCandidates : hits;

  const sorted = candidates
    .map((hit) => {
      const [lat, lng] = hit.Ll;

      return {
        hit,
        dist: distanceMeters(stop.lat, stop.lng, lat, lng),
      };
    })
    .sort((a, b) => a.dist - b.dist);

  const best = sorted[0];

  if (!best || best.dist > 250) return null;

  return best.hit;
}

export async function enrichStopWithCity(stop: Stop): Promise<Stop> {
  if ((stop as any).cityName || (stop as any).taxeZoneName) {
    return stop;
  }

  try {
    const query = stripStopNameForCity(stop.name);
    const hits = await findStops(query, "stop");
    const hit = findBestStopFindHit(stop, hits);
    const cityName = hit?.TaxeZone?.Name?.trim();

    if (!cityName) return stop;

    return {
      ...stop,
      cityName,
      taxeZoneName: cityName,
      parentLat: stop.parentLat ?? stop.lat,
      parentLng: stop.parentLng ?? stop.lng,
    };
  } catch (error) {
    console.error("Failed to enrich stop with city", error);
    return stop;
  }
}
